import React, { PropsWithChildren } from 'react';
import { Button as DsButton, ButtonProps } from '@navikt/ds-react';
import { classNames } from 'utils/classnames';
import { LenkeBase } from 'components/_common/lenke/lenkeBase/LenkeBase';
import { XpImageProps } from 'types/media';
import { XpImage } from 'components/_common/image/XpImage';
import { AnalyticsEvents, AnalyticsEventName, logAnalyticsEvent } from 'utils/analytics';
import { onlyText } from 'utils/react-children';
import { innholdsTypeMap } from 'types/content-props/_content-common';
import { usePageContentProps } from 'store/pageContext';
import { getDecoratorParams } from 'utils/decorator-utils';
import { useLayoutConfig } from 'components/layouts/useLayoutConfig';

import style from './Knapp.module.scss';

type Props = PropsWithChildren<{
    href?: string;
    variant?: ButtonProps['variant'];
    size?: ButtonProps['size'];
    xpIcon?: XpImageProps;
    dsIcon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    fullWidth?: boolean;
    disabled?: boolean;
    prefetch?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    className?: string;
    analyticsEvent?: AnalyticsEventName;
    analyticsComponent?: string;
    analyticsLabel?: string;
    lenkestyling?: boolean;
}>;

export const Knapp = ({
    href,
    variant = 'secondary',
    size = 'medium',
    xpIcon,
    dsIcon,
    iconPosition,
    fullWidth,
    disabled,
    prefetch,
    onClick,
    className,
    children,
    analyticsEvent,
    analyticsComponent = 'Knapp',
    analyticsLabel,
    lenkestyling = false,
}: Props) => {
    const contentProps = usePageContentProps();
    const { context } = getDecoratorParams(contentProps);
    const { layoutConfig } = useLayoutConfig();
    const link = href || '#';
    const analyticsData = {
        komponentId: analyticsComponent,
        seksjon: layoutConfig.title,
        destinasjon: link,
        lenketekst: analyticsLabel || onlyText(children),
        målgruppe: context,
        innholdstype: innholdsTypeMap[contentProps.type],
    };

    return (
        <DsButton
            as={LenkeBase}
            href={link}
            className={classNames(
                style.knapp,
                fullWidth && style.knappFullWidth,
                className,
                lenkestyling && style.lenkestyling
            )}
            onClick={(e) => {
                logAnalyticsEvent(analyticsEvent || AnalyticsEvents.NAVIGERE, analyticsData);
                if (!href) {
                    e.preventDefault();
                }
                onClick?.(e);
            }}
            prefetch={prefetch}
            variant={variant}
            size={size}
            disabled={disabled}
            icon={
                xpIcon ? (
                    <XpImage imageProps={xpIcon} className={style.knapp__icon} maxWidth={64} />
                ) : (
                    dsIcon
                )
            }
            iconPosition={iconPosition}
        >
            {children}
        </DsButton>
    );
};
